const encoder = new TextEncoder();

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function write16(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value, true);
}

function write32(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value, true);
}

function zipDate(date: Date) {
  return ((date.getFullYear() - 1980) << 9)
    | ((date.getMonth() + 1) << 5) | date.getDate();
}

function zipTime(date: Date) {
  return (date.getHours() << 11) | (date.getMinutes() << 5)
    | Math.floor(date.getSeconds() / 2);
}

export function csv(rows: unknown[][]) {
  const escape = (value: unknown) => {
    const text = value == null ? "" : String(value);
    return `"${text.replaceAll('"', '""')}"`;
  };
  return `\ufeff${rows.map((row) => row.map(escape).join(",")).join("\r\n")}\r\n`;
}

export function zip(files: Array<{ name: string; content: string }>) {
  const now = new Date();
  const encoded = files.map((file) => ({
    name: encoder.encode(file.name),
    data: encoder.encode(file.content)
  }));
  const localSize = encoded.reduce((total, file) =>
    total + 30 + file.name.length + file.data.length, 0);
  const centralSize = encoded.reduce((total, file) =>
    total + 46 + file.name.length, 0);
  const output = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(output.buffer);
  const centralEntries: Array<{ file: typeof encoded[number]; offset: number; crc: number }> = [];
  let offset = 0;

  for (const file of encoded) {
    const checksum = crc32(file.data);
    centralEntries.push({ file, offset, crc: checksum });
    write32(view, offset, 0x04034b50);
    write16(view, offset + 4, 20);
    write16(view, offset + 6, 0x0800);
    write16(view, offset + 8, 0);
    write16(view, offset + 10, zipTime(now));
    write16(view, offset + 12, zipDate(now));
    write32(view, offset + 14, checksum);
    write32(view, offset + 18, file.data.length);
    write32(view, offset + 22, file.data.length);
    write16(view, offset + 26, file.name.length);
    write16(view, offset + 28, 0);
    output.set(file.name, offset + 30);
    output.set(file.data, offset + 30 + file.name.length);
    offset += 30 + file.name.length + file.data.length;
  }

  const centralOffset = offset;
  for (const entry of centralEntries) {
    const { file } = entry;
    write32(view, offset, 0x02014b50);
    write16(view, offset + 4, 20);
    write16(view, offset + 6, 20);
    write16(view, offset + 8, 0x0800);
    write16(view, offset + 10, 0);
    write16(view, offset + 12, zipTime(now));
    write16(view, offset + 14, zipDate(now));
    write32(view, offset + 16, entry.crc);
    write32(view, offset + 20, file.data.length);
    write32(view, offset + 24, file.data.length);
    write16(view, offset + 28, file.name.length);
    write16(view, offset + 30, 0);
    write16(view, offset + 32, 0);
    write16(view, offset + 34, 0);
    write16(view, offset + 36, 0);
    write32(view, offset + 38, 0);
    write32(view, offset + 42, entry.offset);
    output.set(file.name, offset + 46);
    offset += 46 + file.name.length;
  }

  write32(view, offset, 0x06054b50);
  write16(view, offset + 4, 0);
  write16(view, offset + 6, 0);
  write16(view, offset + 8, files.length);
  write16(view, offset + 10, files.length);
  write32(view, offset + 12, centralSize);
  write32(view, offset + 16, centralOffset);
  write16(view, offset + 20, 0);
  return output;
}

export function downloadZip(filename: string, files: Array<{ name: string; content: string }>) {
  const archive = zip(files);
  const blob = new Blob([archive], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
